import { ConstraintLayerConfig, isSingleConstraintLayer } from "../types";
import { Layer, LayerTypePrintableProps } from "../../../types";
import { PrintableMapLayer } from "./ConstraintsPrintableMapLayer";
import { useCallback, useEffect, useId, useMemo, useState } from "react";

import { useDesignationsByGeometry } from "react-migration/domains/constraints/designation/hooks/useDesignationsByGeometry";
import { DesignationHatch } from "react-migration/domains/constraints/components/Hatch";
import { DesignationSubTitle, DesignationTitle } from "react-migration/layouts/designations/Designation";
import { ConstraintsLayerTypeProvider, useConstraintsLayerTypeContext } from "../ConstraintsContext";
import { noop } from "lodash";
import { apiDesignationIsVisible } from "../ConstraintsContext/utils";

function ConstraintsPrintableInner({ layer, selection, onLoaded, onUnmount }: LayerTypePrintableProps) {
  const { constraintsStore } = useConstraintsLayerTypeContext();
  const layerStore = constraintsStore.layers[layer.id];

  const [mapRendered, setMapRendered] = useState(false);
  const [mapRenderingError, setMapRenderingError] = useState<string>();

  const onMapLoaded = useCallback((error?: string) => {
    setMapRendered(true);
    setMapRenderingError(error);
  }, []);

  const layerConfig = layer.layerConfig as ConstraintLayerConfig;

  const rootCategories = useMemo(() => {
    if (layerConfig.layerRoots) {
      return layerConfig.layerRoots;
    }
    return isSingleConstraintLayer(layerConfig) ? [layerConfig.rootCategory] : layerConfig.rootCategories;
  }, [layerConfig]);

  // Use the same buffer logic as the workbench (DesignationsSelectionSidebar)
  const intersectionBuffer = layerConfig.intersectionThresholdMeters ?? -1;

  const { designations, loading: designationsLoading } = useDesignationsByGeometry({
    geometry: selection.feature?.geometry ?? null,
    rootCategories,
    bufferMeters: intersectionBuffer,
    attributeKeys: layerConfig.attributesToLoad,
  });

  const visibleDesignations = useMemo(() => {
    const filtered = designations.filter((designation) => apiDesignationIsVisible(designation, layerStore));

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designations, layerStore, layer.id]);

  useEffect(() => {
    return () => {
      onUnmount?.();
    };
  }, [onUnmount]);

  useEffect(() => {
    if (!designationsLoading && layerStore.layerConfigHash && (mapRendered || !visibleDesignations.length)) {
      onLoaded(mapRenderingError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designationsLoading, layer.id, layerStore.layerConfigHash, mapRendered, onLoaded, visibleDesignations.length]);

  return visibleDesignations.length > 0 ? (
    <div className="flex flex-col gap-2">
      <h2 className="text-base">{layer.title}</h2>
      <div className="flex gap-2">
        <div className="flex flex-col grow gap-1">
          {visibleDesignations.map((designation) => (
            <div key={designation.id}>
              <div className="flex items-start gap-2">
                <DesignationHatch id={designation.sub_category_id} />
                <div>
                  <DesignationTitle designation={designation} />
                  <DesignationSubTitle designation={designation} />
                  <p>{designation.sub_category?.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="max-w-5/12">
          <div className="relative aspect-square">
            <PrintableMapLayer id={layer.id} selection={selection} layerConfig={layerConfig} onLoaded={onMapLoaded} />
          </div>
        </div>
      </div>
    </div>
  ) : null;
}

/** Modify `layerProp.id` to prevent clashes on id. Noticed this occurring
 * across flooding & significant constraints both using `policy` id. */
function useLayer(layer: Layer) {
  const id = useId();
  return useMemo<Layer>(() => ({ ...layer, id: `${id}-${layer.id}` }), [id, layer]);
}

function withConstraintsContext(Component: React.ComponentType<LayerTypePrintableProps>) {
  return function ConstraintsContextWrapper({ layer: layerProp, ...props }: LayerTypePrintableProps) {
    const layer = useLayer(layerProp);

    const [layers, visibleLayerIds] = useMemo(() => [[layer], [layer.id]], [layer]);

    return (
      <ConstraintsLayerTypeProvider
        id="CONSTRAINTS_PRINTABLE"
        setDetailSelection={noop}
        setSelection={noop}
        layers={layers}
        visibleLayerIds={visibleLayerIds}
      >
        <Component {...props} layer={layer} />
      </ConstraintsLayerTypeProvider>
    );
  };
}

export const ConstraintsPrintable = withConstraintsContext(ConstraintsPrintableInner);
